import { isPresent, throwErrorUnlessProduction } from '@luminovo/commons';
import { z } from 'zod/v4';

import { isFilterValue } from '../Filters/createFilterValueUpdater';
import { RowData } from '../tanstackTypes';
import { TanStackTableState, UseTanStackTablePaginationProps } from '../type';

export function generateFilterRequest<TData extends RowData, TSharedContext, TRuntype extends z.ZodTypeAny>(
    tableState: TanStackTableState<TData>,
    columns: UseTanStackTablePaginationProps<TData, TSharedContext>['columns'],
    runtype: TRuntype,
): z.infer<TRuntype>[] {
    const result = tableState.columnFilters.map((filter) => {
        const columnDef = columns.find((def) => def.id === filter.id);
        const filterValueTransform = columnDef?.meta?.filterValueTransform ?? ((value: unknown) => value);

        if (!isPresent(columnDef)) {
            throw new Error(`Unknown filter id: ${filter.id}`);
        }

        if (!isPresent(columnDef.id)) {
            throw new Error(`Column id must be defined`);
        }

        if (!isPresent(columnDef.meta) || !isPresent(columnDef.meta?.filterConfig)) {
            throw new Error(`Filter function is not defined for column ${columnDef.id}`);
        }

        if (!isFilterValue(filter.value)) {
            throw new Error(`Invalid filter value for column ${columnDef.id}`);
        }

        const filterRequest = {
            field: columnDef.id,
            operator: columnDef.meta.filterConfig.defaultFilterFn,
            parameter: filterValueTransform(filter.value.value),
        };

        const parseResult = runtype.safeParse(filterRequest);
        if (!parseResult.success) {
            throwErrorUnlessProduction(parseResult.error);
            // In production, we'll return the unvalidated object
            return filterRequest as z.infer<TRuntype>;
        }

        return parseResult.data;
    });

    return result;
}

export function generateSortRequest<TData extends RowData, TSharedContext, TRuntype extends z.ZodTypeAny>(
    tableState: TanStackTableState<TData>,
    columns: UseTanStackTablePaginationProps<TData, TSharedContext>['columns'],
    runtype: TRuntype,
): z.infer<TRuntype>[] {
    const result = tableState.sorting.map((sort) => {
        const columnDef = columns.find((def) => def.id === sort.id);

        if (!isPresent(columnDef)) {
            throw new Error(`Unknown filter id: ${sort.id}`);
        }

        if (!isPresent(columnDef.id)) {
            throw new Error(`Column id must be defined`);
        }

        const sortRequest = {
            field: columnDef.id,
            direction: sort.desc ? 'desc' : 'asc',
        };

        const parseResult = runtype.safeParse(sortRequest);
        if (!parseResult.success) {
            throwErrorUnlessProduction(parseResult.error);
            // In production, we'll return the unvalidated object
            return sortRequest as z.infer<TRuntype>;
        }

        return parseResult.data;
    });

    return result;
}

/** The backend's count for an option: the rows that selecting it as a filter would match. */
export function getOptionCount<
    A extends { field: string; options: Array<{ value: unknown; count: number }> },
    F extends A['field'],
    T,
    U = T,
>(
    option: T,
    aggregations: A[] | undefined,
    field: F,
    valueSelector: (option: T) => U = (o) => o as unknown as U,
): number | undefined {
    return aggregations?.find((a) => a.field === field)?.options.find((i) => i.value === valueSelector(option))?.count;
}
